import { css } from "@emotion/react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Router } from "../App";
import { trpcClient } from "../lib/trpc";
import { useOrganisation } from "../lib/organisation";
import { slugify } from "../lib/slugify";
import { Button } from "@/components/button";
import { Modal, ConfirmModal } from "@/components/modal";
import type { GridValues } from "@/types/grid";
import { Phone, Fire, Trophy } from "@phosphor-icons/react";
import {
  appContainer,
  appHeader,
  appTitle,
  appSubtitle,
  pageContent,
  pageSectionTitle,
  pageDescription,
  emptyState,
  loading,
  error,
  formGroup,
  formLabel,
  formInput,
  formError,
  modalActions,
  modalDescription,
  actionButtons,
} from "@/styles/shared";
import {
  bingoGrid,
  bingoRow,
  bingoCell,
  bingoCellEmpty,
  bingoCellDrawn,
} from "@/styles/bingo-grid";

const drawGrid = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  background: var(--container-bg);
  border: 2px solid var(--input-border);
  border-radius: 12px;
  padding: 12px;
  box-shadow: 0 4px 12px var(--shadow-medium);
  width: 100%;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 640px) {
    padding: 6px;
    border-radius: 8px;
    gap: 1px;
  }
`;

const drawGridRow = css`
  display: grid;
  grid-template-columns: repeat(10, 1fr);
  gap: 2px;

  @media (max-width: 640px) {
    gap: 1px;
  }
`;

const drawGridCell = css`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-secondary);
  background: var(--color-bg-warm);
  border: 1px solid var(--input-border);
  transition: all 0.2s ease;

  @media (max-width: 640px) {
    font-size: 0.75rem;
    border-radius: 4px;
  }
`;

const drawGridCellClickable = css`
  cursor: pointer;

  &:hover {
    background: var(--input-border);
    color: var(--text-primary);
  }
`;

const drawGridCellDrawn = css`
  background: var(--color-primary);
  color: #FFFFFF;
  font-weight: 500;
  border-color: transparent;
  box-shadow: 0 2px 6px var(--shadow-medium);
`;

const drawGridCellEmpty = css`
  visibility: hidden;
`;

const drawGridCellExcluded = css`
  background: var(--color-bg-warm);
  color: var(--text-muted);
  opacity: 0.4;
  cursor: not-allowed;
  text-decoration: line-through;
  border-color: transparent;
`;

const winningGridCard = css`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--input-border);
  background: var(--color-bg-warm);

  @media (max-width: 640px) {
    padding: 12px;
  }
`;

const winningGridHeader = css`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const winningGridIdentifier = css`
  font-weight: 500;
  color: var(--text-primary);
`;

const winningGridContent = css`
  display: flex;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 12px;
  }
`;

const winningGridSection = css`
  flex: 2;
  min-width: 0;
`;

const winningGridContactSection = css`
  flex: 1;
  min-width: 0;
  padding: 12px;
  background: var(--container-bg);
  border-radius: 8px;
  border: 1px solid var(--input-border);

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const winningGridContactTitle = css`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-tertiary);
  margin-bottom: 8px;
  font-weight: 600;
`;

const winningGridContactName = css`
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 4px;
`;

const winningGridContactPhone = css`
  font-size: 14px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 6px;

  a {
    color: var(--color-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const winningGridsTitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-xl);
  margin-bottom: 16px;
  font-weight: 700;
  color: var(--text-primary);

  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const winningGridsList = css`
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const gameLayout = css`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const gameLayoutRow1 = css`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const gameLayoutDraw = css`
  flex: 2;
  min-width: 0;
`;

const gameLayoutWinners = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const winningGridsRow = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const almostWinningRow = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const almostWinningGrid = css`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const almostWinningCount = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-bottom: 4px;
`;

const almostWinningPanelTitle = css`
  font-family: var(--font-heading);
  font-size: var(--text-lg);
  font-weight: 700;
  color: var(--color-primary);
  display: flex;
  align-items: center;
  gap: 8px;

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const compactCard = css`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--input-border);
  background: var(--color-bg-warm);
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const winningCompactCard = css`
  padding: 12px;
  border-radius: 8px;
  border: 2px solid #d4af37;
  background: linear-gradient(135deg, #fffef0 0%, #fff9e6 100%);
  display: flex;
  flex-direction: column;
  gap: 6px;
  box-shadow: 0 0 16px rgba(212, 175, 55, 0.4), 0 0 32px rgba(212, 175, 55, 0.2);
  position: relative;

  &::before {
    content: "";
    position: absolute;
    inset: -2px;
    border-radius: 10px;
    background: linear-gradient(135deg, #ffd700, #d4af37, #b8860b);
    z-index: -1;
    opacity: 0.3;
  }
`;

const winningBadge = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #ffd700, #d4af37);
  color: #5c4813;
  font-size: 12px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 12px;
  margin-left: auto;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const compactCardHeader = css`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const compactCardIdentifier = css`
  font-weight: 500;
  color: var(--text-primary);
`;

const compactCardNumbers = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const compactCardNumber = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  background: var(--container-bg);
  color: var(--text-primary);
  border: 1px solid var(--input-border);
`;

const compactCardLabel = css`
  font-size: 12px;
  color: var(--text-tertiary);
  font-weight: 500;
`;

const almostWinningBadge = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--color-primary);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: auto;
`;

const radioOptionsGroup = css`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const radioOption = css`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid var(--input-border);
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background 0.2s ease;

  & input[type="radio"] {
    margin-top: 3px;
    flex-shrink: 0;
  }

  @media (max-width: 640px) {
    padding: 10px;
  }
`;

const radioOptionSelected = css`
  border-color: var(--color-primary);
  background: var(--color-bg-warm);
`;

const radioOptionLabel = css`
  font-weight: 500;
  color: var(--text-primary);
`;

const radioOptionDesc = css`
  font-size: 14px;
  color: var(--text-secondary);
`;

const radioOptionSub = css`
  margin-top: 8px;
`;

const radioOptionDisclaimer = css`
  margin-top: 8px;
  font-size: 13px;
  color: var(--error-text);
  font-style: italic;
`;

const modeToggleGroup = css`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;

  & > button {
    flex: 1;
  }

  @media (max-width: 640px) {
    flex-direction: column;
  }
`;

const selectMarginTop = css`
  margin-top: 4px;
`;

const drawErrorStyle = css`
  margin-bottom: 12px;
`;

export const gameTypeValues = ["line", "grid"] as const;
export type GameType = (typeof gameTypeValues)[number];

export const gameStatusValues = ["Ongoing", "Completed"] as const;
export type GameStatus = (typeof gameStatusValues)[number];

export type Game = {
  id: string;
  type: GameType;
  status: GameStatus;
  eventId: string;
  excludedNumbers: number[];
  createdAt: Date;
  updatedAt: Date;
};

type Draw = {
  id: string;
  eventId: string;
  numbers: number[];
  status: "Ongoing" | "Completed";
  createdAt: Date;
};

type WinningGrid = {
  gridId: string;
  identifier: string;
  numbers: GridValues;
  playedAutomatically: boolean;
  playerInfo: { firstname: string; lastname: string; phone: string } | null;
};

type AlmostWinningGrid = WinningGrid & {
  missingCount: number;
};

interface GamePageProps {
  id: string;
}

function DrawNumbersGrid({
  numbers,
  excludedNumbers = [],
  onNumberClick,
}: {
  numbers: number[];
  excludedNumbers?: number[];
  onNumberClick: (num: number) => void;
}) {
  const drawnSet = new Set(numbers);
  const excludedSet = new Set(excludedNumbers);
  const rows = Array.from({ length: 10 }, (_, row) =>
    Array.from({ length: 10 }, (_, col) => row * 10 + col + 1),
  );

  return (
    <div css={drawGrid}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} css={drawGridRow}>
          {row.map((num) => {
            if (num > 90) {
              return <div key={num} css={[drawGridCell, drawGridCellEmpty]} />;
            }
            const isDrawn = drawnSet.has(num);
            const isExcluded = excludedSet.has(num);

            // Excluded numbers are greyed out and not clickable
            if (isExcluded) {
              return (
                <div key={num} css={[drawGridCell, drawGridCellExcluded]}>
                  {num}
                </div>
              );
            }

            return (
              <div
                key={num}
                css={[drawGridCell, isDrawn ? drawGridCellDrawn : drawGridCellClickable]}
                onClick={isDrawn ? undefined : () => onNumberClick(num)}
              >
                {num}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/**
 * Sanitize phone number for tel: href (keep only digits, +, spaces, dashes, parentheses)
 */
function sanitizePhone(phone: string): string {
  return phone.replace(/[^\d+\s\-()]/g, "");
}

/**
 * Compute the specific missing numbers for a grid.
 * - "line" game: find the row closest to completion, return its missing numbers
 * - "grid" game: return all missing numbers across all rows
 */
function computeMissingNumbers(
  gridNumbers: GridValues,
  drawnNumbers: Set<number>,
  gameType: GameType,
): number[] {
  const rowsMissing = gridNumbers.map((row) =>
    row.filter((cell): cell is number => cell !== null && !drawnNumbers.has(cell)),
  );

  if (gameType === "line") {
    let bestRow: number[] = rowsMissing[0] ?? [];
    for (const row of rowsMissing) {
      if (row.length < bestRow.length) {
        bestRow = row;
      }
    }
    return bestRow;
  }

  return rowsMissing.flat();
}

function WinningGridCard({ grid, drawnNumbers }: { grid: WinningGrid; drawnNumbers: Set<number> }) {
  const { t } = useTranslation();

  return (
    <li css={winningGridCard}>
      <div css={winningGridHeader}>
        <div css={winningGridIdentifier}>{grid.identifier}</div>
      </div>
      <div css={winningGridContent}>
        <div css={winningGridSection}>
          <div css={bingoGrid}>
            {grid.numbers.map((row, rowIndex) => (
              <div key={rowIndex} css={bingoRow}>
                {row.map((cell, cellIndex) => {
                  const isEmpty = cell === null;
                  const isDrawn = cell !== null && drawnNumbers.has(cell);
                  return (
                    <div
                      key={cellIndex}
                      css={[bingoCell, isEmpty && bingoCellEmpty, isDrawn && bingoCellDrawn]}
                    >
                      {cell ?? ""}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        {grid.playerInfo && (
          <div css={winningGridContactSection}>
            <div css={winningGridContactTitle}>{t("game.contact")}</div>
            <div css={winningGridContactName}>
              {grid.playerInfo.firstname} {grid.playerInfo.lastname}
            </div>
            <div css={winningGridContactPhone}>
              <Phone size={16} /> <a href={`tel:${sanitizePhone(grid.playerInfo.phone)}`}>{grid.playerInfo.phone}</a>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

function CompactWinningCard({ grid }: { grid: WinningGrid }) {
  const { t } = useTranslation();

  return (
    <div css={winningCompactCard}>
      <div css={compactCardHeader}>
        <Trophy size={18} weight="fill" color="#d4af37" />
        <span css={compactCardIdentifier}>{grid.identifier}</span>
        <span css={winningBadge}>{t("game.winner")}</span>
      </div>
      {grid.playerInfo && (
        <div css={compactCardLabel}>
          {grid.playerInfo.firstname} {grid.playerInfo.lastname}
        </div>
      )}
    </div>
  );
}

function CompactAlmostWinningCard({
  grid,
  drawnNumbers,
  gameType,
}: {
  grid: AlmostWinningGrid;
  drawnNumbers: Set<number>;
  gameType: GameType;
}) {
  const { t } = useTranslation();
  const missingNumbers = computeMissingNumbers(grid.numbers, drawnNumbers, gameType);

  return (
    <div css={compactCard}>
      <div css={compactCardHeader}>
        <span css={compactCardIdentifier}>{grid.identifier}</span>
        <span css={almostWinningBadge}>
          {t("game.missingNumbers", { count: grid.missingCount })}
        </span>
      </div>
      {missingNumbers.length > 0 && (
        <div>
          <div css={compactCardLabel}>{t("game.missingNumbersList")}</div>
          <div css={compactCardNumbers}>
            {missingNumbers.map((num) => (
              <span key={num} css={compactCardNumber}>
                {num}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function GamePage({ id }: GamePageProps) {
  const { t } = useTranslation();
  const { currentOrganisation } = useOrganisation();
  const orgSlug = slugify(currentOrganisation?.name ?? "");
  const [game, setGame] = useState<Game | null>(null);
  const [draw, setDraw] = useState<Draw | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [numberToDraw, setNumberToDraw] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [drawError, setDrawError] = useState<string | null>(null);
  const [winningGrids, setWinningGrids] = useState<WinningGrid[]>([]);
  const [almostWinningGrids, setAlmostWinningGrids] = useState<AlmostWinningGrid[]>([]);
  const [newGameModal, setNewGameModal] = useState<null | "choose" | "confirmUnmask" | "confirm">(null);
  const [showWinnerPrompt, setShowWinnerPrompt] = useState(false);
  const [selectedGameType, setSelectedGameType] = useState<GameType>("line");
  const [unmaskGrids, setUnmaskGrids] = useState(false);
  const [newGameProcessing, setNewGameProcessing] = useState(false);

  const fetchWinningGrids = useCallback(() => {
    if (!game) return;
    return trpcClient.getWinningGrids
      .query({ gameId: game.id })
      .then((grids) => setWinningGrids(grids))
      .catch(() => null);
  }, [game]);

  const fetchAlmostWinningGrids = useCallback(() => {
    if (!game) return;
    return trpcClient.getAlmostWinningGrids
      .query({ gameId: game.id, maxMissing: 2 })
      .then((grids) => setAlmostWinningGrids(grids))
      .catch(() => null);
  }, [game]);

  const fetchDraw = useCallback(() => {
    if (!game) return;
    return trpcClient.getActiveDraw
      .query({ eventId: game.eventId })
      .then((drawResult) => {
        setDraw({
          ...drawResult,
          createdAt: new Date(drawResult.createdAt),
        });
      })
      .catch(() => null);
  }, [game]);

  useEffect(() => {
    setIsLoading(true);
    trpcClient.getGame
      .query({ gameId: id })
      .then((gameResult) => {
        setGame({
          ...gameResult,
          createdAt: new Date(gameResult.createdAt),
          updatedAt: new Date(gameResult.updatedAt),
        });
        setErr(null);
      })
      .catch((e) => {
        setErr(e instanceof Error ? e.message : "Failed to load game");
        console.error("Error fetching game:", e);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  useEffect(() => {
    fetchDraw();
    fetchWinningGrids();
    fetchAlmostWinningGrids();
  }, [game]);

  function requestDraw(number: number) {
    setDrawError(null);
    setNumberToDraw(number);
    if (winningGrids.length > 0) {
      setShowWinnerPrompt(true);
    }
  }

  function handleContinueDraw() {
    setShowWinnerPrompt(false);
    // numberToDraw is already set, confirm modal will show
  }

  function handleStartNewGameInstead() {
    setShowWinnerPrompt(false);
    setNumberToDraw(null);
    setNewGameModal("choose");
  }

  async function handleConfirmDraw() {
    if (!draw || numberToDraw === null) return;
    setSubmitting(true);
    setDrawError(null);
    try {
      await trpcClient.drawNumber.mutate({
        eventId: draw.eventId,
        number: numberToDraw,
      });
      setNumberToDraw(null);
      await fetchDraw();
      await Promise.all([fetchWinningGrids(), fetchAlmostWinningGrids()]);
    } catch (e) {
      setDrawError(e instanceof Error ? e.message : "Failed to draw number");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewGameConfirm() {
    if (!game) return;
    setNewGameProcessing(true);
    try {
      const newGame = await trpcClient.startGame.mutate({
        eventId: game.eventId,
        gameType: selectedGameType,
        unmaskGrids,
      });
      setNewGameModal(null);
      setUnmaskGrids(false);
      Router.push("Game", { orgSlug, id: newGame.id });
    } catch (e) {
      setDrawError(e instanceof Error ? e.message : "Failed to process action");
    } finally {
      setNewGameProcessing(false);
    }
  }

  function handleNewGameNext() {
    if (unmaskGrids) {
      setNewGameModal("confirmUnmask");
    } else {
      setNewGameModal("confirm");
    }
  }

  return (
    <div css={appContainer}>
      <header css={appHeader}>
        <div>
          <h1 css={appTitle}>{t("game.title")}</h1>
          <p css={appSubtitle}>{t("game.subtitle")}</p>
        </div>
        {game && (
          <div css={actionButtons}>
            <Button
              variant="secondary"
              onClick={() => window.open(`/live/${game.eventId}`, "_blank")}
            >
              {t("game.openLiveDisplay")}
            </Button>
            <Button variant="primary" onClick={() => setNewGameModal("choose")}>
              {t("game.newGame")}
            </Button>
          </div>
        )}
      </header>

      <main css={pageContent}>
        {isLoading && <p css={loading}>{t("game.loading")}</p>}
        {err && <p css={error}>{t("common.error", { message: err })}</p>}
        {!isLoading && !err && game && (
          <section>
            <h3 css={pageSectionTitle}>{t(`gameType.${game.type}`)}</h3>
            <p css={pageDescription}>
              {t("game.status", { status: t(`gameStatus.${game.status}`) })}
            </p>

            {drawError && <p css={[formError, drawErrorStyle]}>{drawError}</p>}

            <div css={gameLayout}>
              {/* Row 1: Draw grid (2/3) + Winners list (1/3) */}
              <div css={gameLayoutRow1}>
                <div css={gameLayoutDraw}>
                  {draw ? (
                    <DrawNumbersGrid
                      numbers={draw.numbers}
                      excludedNumbers={game.excludedNumbers}
                      onNumberClick={requestDraw}
                    />
                  ) : (
                    <p css={emptyState}>{t("game.noActiveDraw")}</p>
                  )}
                </div>

                {winningGrids.length > 0 && (
                  <div css={gameLayoutWinners}>
                    <h3 css={almostWinningPanelTitle}>
                      <Trophy size={20} weight="fill" color="#d4af37" /> {t("game.winnersTitle")}
                    </h3>
                    {winningGrids.map((grid) => (
                      <CompactWinningCard key={grid.gridId} grid={grid} />
                    ))}
                  </div>
                )}
              </div>

              {/* Row 2: Winning grids with numbers */}
              {winningGrids.length > 0 && (
                <div css={winningGridsRow}>
                  <h3 css={winningGridsTitle}>
                    <Trophy size={20} weight="fill" /> {t("game.winningGridsTitle", { count: winningGrids.length })}
                  </h3>
                  <ul css={winningGridsList}>
                    {winningGrids.map((grid) => (
                      <WinningGridCard
                        key={grid.gridId}
                        grid={grid}
                        drawnNumbers={new Set(draw?.numbers)}
                      />
                    ))}
                  </ul>
                </div>
              )}

              {/* Row 3: Almost winning grids (4x3 max) */}
              {almostWinningGrids.length > 0 && (
                <div css={almostWinningRow}>
                  <h3 css={almostWinningPanelTitle}>
                    <Fire size={20} weight="fill" /> {t("game.almostWinningTitle")}
                  </h3>
                  <p css={almostWinningCount}>
                    {t("game.almostWinningCount", { count: almostWinningGrids.length })}
                  </p>
                  <div css={almostWinningGrid}>
                    {almostWinningGrids.slice(0, 12).map((grid) => (
                      <CompactAlmostWinningCard
                        key={grid.gridId}
                        grid={grid}
                        drawnNumbers={new Set(draw?.numbers)}
                        gameType={game.type}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </main>

      {/* Winner prompt modal - shown when there's a winner and user tries to draw */}
      <Modal
        open={showWinnerPrompt}
        onClose={() => {
          setShowWinnerPrompt(false);
          setNumberToDraw(null);
        }}
        title={t("game.winnerExistsTitle")}
      >
        <p css={modalDescription}>{t("game.winnerExistsMessage")}</p>
        <div css={modalActions}>
          <Button variant="secondary" onClick={handleContinueDraw}>
            {t("game.continueDrawing")}
          </Button>
          <Button variant="primary" onClick={handleStartNewGameInstead}>
            {t("game.newGame")}
          </Button>
        </div>
      </Modal>

      {/* Confirm draw modal */}
      <Modal
        open={numberToDraw !== null && !showWinnerPrompt}
        onClose={() => !submitting && setNumberToDraw(null)}
        title={t("game.confirmDraw")}
      >
        <p css={modalDescription}>{t("game.drawNumber", { number: numberToDraw })}</p>
        {drawError && <p css={formError}>{drawError}</p>}
        <div css={modalActions}>
          <Button
            variant="secondary"
            onClick={() => setNumberToDraw(null)}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleConfirmDraw} disabled={submitting}>
            {submitting ? t("game.drawing") : t("common.confirm")}
          </Button>
        </div>
      </Modal>

      <Modal
        open={newGameModal === "choose"}
        onClose={() => {
          if (!newGameProcessing) {
            setNewGameModal(null);
            setUnmaskGrids(false);
          }
        }}
        title={t("game.newGameChooseTitle")}
      >
        <div css={formGroup}>
          <label css={formLabel}>{t("game.gameTypeLabel")}</label>
          <select
            value={selectedGameType}
            onChange={(e) => setSelectedGameType(e.target.value as GameType)}
            css={[formInput, selectMarginTop]}
          >
            {gameTypeValues.map((type) => (
              <option key={type} value={type}>
                {t(`gameType.${type}`)}
              </option>
            ))}
          </select>
        </div>

        <div css={radioOptionsGroup}>
          <label
            css={[radioOption, unmaskGrids && radioOptionSelected]}
            onClick={() => setUnmaskGrids(!unmaskGrids)}
          >
            <input
              type="checkbox"
              checked={unmaskGrids}
              onChange={(e) => setUnmaskGrids(e.target.checked)}
            />
            <div>
              <div css={radioOptionLabel}>{t("game.unmaskGridsOption")}</div>
              <div css={radioOptionDesc}>{t("game.unmaskGridsDesc")}</div>
              {unmaskGrids && (
                <p css={radioOptionDisclaimer}>{t("game.unmaskGridsWarning")}</p>
              )}
            </div>
          </label>
        </div>

        <div css={modalActions}>
          <Button variant="secondary" onClick={() => {
            setNewGameModal(null);
            setUnmaskGrids(false);
          }}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" onClick={handleNewGameNext}>
            {t("common.confirm")}
          </Button>
        </div>
      </Modal>

      <ConfirmModal
        open={newGameModal === "confirmUnmask"}
        onClose={() => setNewGameModal("choose")}
        onConfirm={() => setNewGameModal("confirm")}
        title={t("game.confirmUnmaskTitle")}
        message={t("game.confirmUnmaskMessage")}
        confirmLabel={t("common.confirm")}
      />

      <ConfirmModal
        open={newGameModal === "confirm"}
        onClose={() => setNewGameModal(unmaskGrids ? "confirmUnmask" : "choose")}
        onConfirm={handleNewGameConfirm}
        title={t("game.confirmTitle")}
        message={unmaskGrids ? t("game.confirmNewGameWithUnmask") : t("game.confirmNewGame")}
        loading={newGameProcessing}
        confirmLabel={newGameProcessing ? t("game.processing") : t("common.confirm")}
      />
    </div>
  );
}
